import {
  type ActiveCampaignPlansRes,
  type ActiveOfferSetRes,
  type ActiveOfferSetTemplatesRes,
  type ActivePlanRes,
  type SiteResponse,
} from '@on3/api/src';
import { useAuth, useSite } from '@on3/ui-lib/index';
import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from 'react';

type SubscriptionContextType = {
  allowSubs: boolean;
  isSubscribed: boolean;
  isSubscribedToSite: boolean;
  isPremium: boolean;
  osKey: number;
  osSiteKey: number;
  osSite: SiteResponse;
  plans: ActiveCampaignPlansRes;
  templates: ActiveOfferSetTemplatesRes;
  subscribeText?: string | null;
  setOfferSetSite: (site: SiteResponse) => void;
  setOfferSet: (offerSet: ActiveOfferSetRes | null) => void;
  getPlanById: (planId?: string | null) => ActivePlanRes | undefined;
};

const initialValues: SubscriptionContextType = {
  allowSubs: true,
  isSubscribed: false,
  isSubscribedToSite: false,
  isPremium: false,
  osKey: 0,
  osSiteKey: 0,
  osSite: {} as SiteResponse,
  plans: {},
  templates: {},
  subscribeText: 'Join Now',
  setOfferSetSite: () => {},
  setOfferSet: () => {},
  getPlanById: () => undefined,
};

const SubscriptionContext =
  createContext<SubscriptionContextType>(initialValues);

export const SubscriptionProvider = ({
  children,
  site,
  subscriptionData,
}: {
  children: React.ReactNode;
  site?: SiteResponse | null;
  subscriptionData?: ActiveOfferSetRes | null;
}) => {
  const { user } = useAuth();
  const { currentSite } = useSite();

  const [offerSet, setOfferSet] = useState<ActiveOfferSetRes | null>(
    subscriptionData || null,
  );
  const [offerSetSite, setOfferSetSite] = useState<SiteResponse>(
    site || currentSite,
  );
  const { allowSubs, subscribeText } = offerSetSite || currentSite || {};

  const isSubscribedToSite = useMemo(
    () => user?.si?.includes(offerSetSite.key),
    [offerSetSite, user],
  );

  useEffect(() => {
    if (site) return;

    setOfferSetSite(currentSite);
  }, [site, currentSite]);

  const getPlanById = useCallback(
    (planId?: string | null) => {
      if (!planId) return offerSet?.plans?.annualPremium;

      return Object.values(offerSet?.plans || {}).find(
        (p: ActivePlanRes) => p?.id === planId,
      );
    },
    [offerSet?.plans],
  );

  return (
    <SubscriptionContext.Provider
      value={{
        allowSubs,
        isSubscribed: !!user?.has,
        isSubscribedToSite: !!isSubscribedToSite,
        isPremium: !!user?.st?.includes('premium'),
        osKey: offerSet?.key || 0,
        osSiteKey: offerSet?.siteKey || 0,
        osSite: offerSetSite,
        plans: offerSet?.plans || initialValues.plans,
        templates: offerSet?.templates || initialValues.templates,
        subscribeText: subscribeText || initialValues.subscribeText,
        setOfferSetSite,
        setOfferSet,
        getPlanById,
      }}
    >
      {children}
    </SubscriptionContext.Provider>
  );
};

export const useSubscribe = () => {
  return useContext(SubscriptionContext);
};
